import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { card, sub, sectionLabel, pill, RGB, STATUS_COLOR, Icon, ICONS, errMsg } from '../ext/extUi';
import ExtMatchFeed, { FEED_TYPES } from '../ext/ExtMatchFeed';
import { TeamTable, RoundStrip } from '../ext/ExtMatchView';
import ExtPlayerStats, { FLUX3_COLS } from '../ext/ExtPlayerStats';

// Panel match → Live view (/admin/matches/:id/live): the external tournaments' match screen on panel
// data — live scoreboard (HP, armor, equipment, money, K/D/A, ADR, KAST, rating), round history and the
// full match feed with highlights (ace, clutches, Zeus / knife kills, map / match point, defuse start).
// Data: GET /api/v1/matches/:id, …/scoreboard, …/rounds, …/events; live over WS /ws/v1?matches=<id>.

const SIDE = { CT: { color: '#5b9cf5', rgb: '91,156,245' }, T: { color: '#e8a33d', rgb: '232,163,61' } };
const FEED = FEED_TYPES; // Zeus / knife kills show as pills on their kill row

// panel knife-round events keep round 1 with data.knife_round; the feed shows them in its knife card
const forFeed = (e) => (e.data && e.data.knife_round ? { ...e, round: null } : e);
// equipment is null while unknown (not seen yet / broadcast delay) — the table wants empty values
const forTable = (rows) => (rows || []).map(p => ({ ...p, grenades: p.grenades || [], money: p.money ?? null }));

export default function MatchLiveView() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [match, setMatch] = useState(null);
  const [sb, setSb] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [events, setEvents] = useState([]);
  const [conn, setConn] = useState('connecting');
  const [statsTick, setStatsTick] = useState(0); // re-reads the per-map player stats after a round end / restore
  const [error, setError] = useState('');
  const wsRef = useRef(null);

  const loadRounds = useCallback(() => axios.get(`/api/v1/matches/${id}/rounds`).then(r => setRounds(r.data.data || [])).catch(() => { }), [id]);
  const loadAll = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([axios.get(`/api/v1/matches/${id}`), axios.get(`/api/v1/matches/${id}/scoreboard`)]);
      const all = [];
      let since = 0;
      for (let i = 0; i < 50; i++) {
        const e = await axios.get(`/api/v1/matches/${id}/events`, { params: { types: FEED.join(','), limit: 2000, since } });
        all.push(...(e.data.data || []));
        since = e.data.last_id;
        if (!e.data.has_more) break;
      }
      setMatch(m.data); setSb(s.data); setEvents(all.map(forFeed)); setError('');
      loadRounds();
    } catch (err) { setError(errMsg(err, t('ext.loadFailed', 'Failed to load'))); }
  }, [id, t, loadRounds]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // live: /ws/v1 (snapshot → events, state changes, scoreboard pushes); reconnects with backoff
  useEffect(() => {
    let closed = false, retry = 0, timer = null;
    const connect = () => {
      const token = localStorage.getItem('token');
      const ws = new WebSocket(`${window.location.origin.replace(/^http/, 'ws')}/ws/v1?token=${encodeURIComponent(token || '')}&matches=${id}&scoreboard=1`);
      wsRef.current = ws;
      ws.onopen = () => { retry = 0; setConn('live'); };
      ws.onmessage = (msg) => {
        let m; try { m = JSON.parse(msg.data); } catch (_) { return; }
        if (m.type === 'snapshot') { setMatch(prev => ({ ...(prev || {}), ...m.match })); if (m.scoreboard) setSb(m.scoreboard); }
        else if (m.type === 'scoreboard') setSb(m.scoreboard);
        else if (m.type === 'state') setMatch(prev => (prev ? { ...prev, ...m.state } : prev));
        else if (m.type === 'event') {
          const e = m.event;
          if (e.type === 'round_restored' || e.type === 'map_reset') { loadAll(); setStatsTick(n => n + 1); return; } // earlier events were voided
          if (e.type === 'seating_update') setMatch(prev => (prev ? { ...prev, seating: e.data.seating } : prev));
          if (FEED.includes(e.type)) setEvents(prev => (prev.some(x => x.id === e.id) ? prev : [...prev, forFeed(e)]));
          if (e.type === 'round_end') { loadRounds(); setStatsTick(n => n + 1); }
        }
      };
      ws.onclose = () => {
        setConn('reconnecting');
        if (closed) return;
        retry = Math.min(retry + 1, 6);
        timer = setTimeout(connect, 500 * 2 ** retry);
      };
    };
    connect();
    return () => { closed = true; clearTimeout(timer); if (wsRef.current) wsRef.current.close(); };
  }, [id, loadAll, loadRounds]);

  if (!match) return <div style={sub}>{error || t('common.loading', 'Loading...')}</div>;

  const s1 = match.teams.team1, s2 = match.teams.team2;
  const side1 = (sb && sb.sides && sb.sides.team1) || s1.side;
  const side2 = (sb && sb.sides && sb.sides.team2) || s2.side;
  const score = (sb && sb.score) || { team1: s1.score, team2: s2.score };
  const mapIndex = sb ? sb.map_index : match.current_map_index;
  const bigScore = (val, side) => <span style={{ fontFamily: 'var(--font-display, sans-serif)', fontWeight: 800, fontSize: '2.2rem', color: side ? SIDE[side].color : 'var(--text-primary)', minWidth: 48, textAlign: 'center' }}>{val}</span>;

  return (
    <div>
      <div style={{ marginBottom: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <Link to={`/admin/matches/${id}`} style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><Icon d={ICONS.back} /> {t('extSeating.backToMatch', 'Back to the match')}</Link>
      </div>

      <div style={{ ...card, padding: '1.1rem 1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <span style={pill(STATUS_COLOR[match.phase] || RGB.gray)}>{t(`ext.phase.${match.phase}`, match.phase)}</span>
          {match.current_round && <span style={pill(RGB.gray)}>{t('ext.roundN', 'Round {{n}}', { n: match.current_round })}</span>}
          {match.pause && <span style={pill(RGB.amber)}>{t('ext.paused', 'Paused')}: {match.pause.type}</span>}
          {sb && sb.bomb && <span style={pill(SIDE.T.rgb)}>{t('ext.bombPlanted', 'Bomb planted {{site}}', { site: sb.bomb.site || '' })}</span>}
          <span style={pill(RGB.gray)}>BO{match.best_of}</span>
          {(match.match_point || match.map_point) && (
            <span style={pill(match.match_point ? RGB.red : RGB.amber)}>
              {match.match_point
                ? t('ext.feed.matchPoint', 'Match point: {{team}}', { team: match.teams[match.match_point].name })
                : t('ext.feed.mapPoint', 'Map point: {{team}}', { team: match.teams[match.map_point].name })}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <span style={pill(conn === 'live' ? RGB.green : RGB.amber)}>{conn === 'live' ? t('ext.connLive', 'Live') : t('ext.connReconnecting', 'Reconnecting…')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px', textAlign: 'right' }}>
            <div style={{ fontWeight: 800, fontSize: '1.15rem', fontFamily: 'var(--font-display, sans-serif)', color: 'var(--text-primary)' }}>{s1.name}</div>
            {side1 && <span style={pill(SIDE[side1].rgb)}>{side1}</span>}
          </div>
          {bigScore(score.team1, side1)}
          <div style={{ textAlign: 'center' }}>
            <div style={sectionLabel}>{sb && sb.map ? sb.map.replace(/^de_/, '') : ''}</div>
            {match.best_of > 1 && <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{match.series.team1} – {match.series.team2}</div>}
          </div>
          {bigScore(score.team2, side2)}
          <div style={{ flex: '1 1 200px' }}>
            <div style={{ fontWeight: 800, fontSize: '1.15rem', fontFamily: 'var(--font-display, sans-serif)', color: 'var(--text-primary)' }}>{s2.name}</div>
            {side2 && <span style={pill(SIDE[side2].rgb)}>{side2}</span>}
          </div>
        </div>
        {match.maps && match.maps.length > 0 && (
          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '0.9rem' }}>
            {match.maps.map(mp => (
              <span key={mp.index} style={pill(mp.status === 'live' ? RGB.green : mp.status === 'finished' ? RGB.gray : RGB.blue)}>
                {(mp.name || '?').replace(/^de_/, '')}{mp.score ? ` ${mp.score.team1}-${mp.score.team2}` : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      {sb && (
        <>
          <TeamTable team={s1} players={forTable(sb.teams.team1)} side={side1} roundLive={sb.round_live} />
          <TeamTable team={s2} players={forTable(sb.teams.team2)} side={side2} roundLive={sb.round_live} />
        </>
      )}
      <RoundStrip rounds={rounds} mapIndex={mapIndex} />
      {/* whole match or one map (the ext match page's card on GET /api/v1/matches/:id/players) */}
      <ExtPlayerStats match={match} url={`/api/v1/matches/${id}/players`} refreshKey={statsTick} extraCols={FLUX3_COLS} />
      {events.length > 0
        ? <ExtMatchFeed events={events} match={match} />
        : <div style={card}><div style={sub}>{t('liveView.noEvents', 'No events yet. They appear here as the match is played.')}</div></div>}
    </div>
  );
}
